import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useAppMode } from '../../contexts/AppModeContext';
import { rememberReturnPath } from '../../utils/offlineSession';
import Loading from '../common/Loading';
import '../../styles/common.css';

/**
 * Higher-order component that protects routes requiring authentication
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @param {boolean} props.requireCanvas - Whether the route requires Canvas credentials
 * @param {boolean} props.allowGuest - Whether to allow guest mode access (default: true)
 */
const ProtectedRoute = ({ children, requireCanvas = false, allowGuest = true }) => {
  const { currentUser, hasCanvasCredentials, loading: authLoading, sessionRecovering } = useAuth();
  const location = useLocation();
  const {
    coursesMode,
    initializing: canvasLoading,
    getActiveCredentials,
    pendingUserSelection
  } = useAppMode();

  // Don't render anything while checking auth state
  if (authLoading || canvasLoading) {
    return <Loading message="Loading..." />;
  }

  // Check if user has access (either authenticated, or guest/demo mode if allowed)
  // Task 6.0ag: offline mode is the user's own snapshot, not a guest -- with no
  // user it means the session has ended, so it goes to /login like 'auth'
  // does. The exception is 6.0af's background retry (`sessionRecovering`): the
  // snapshot keeps rendering until that settles.
  const userlessModeHasAccess =
    coursesMode === 'offline' ? !!sessionRecovering : coursesMode !== 'auth';
  const hasAccess = currentUser || (allowGuest && userlessModeHasAccess);

  // If no access at all, redirect to login, remembering the page so signing
  // in again returns to it (Login reads it from sessionStorage, which also
  // survives the forced-logout hard redirect that carries no router state).
  if (!hasAccess) {
    rememberReturnPath(`${location.pathname}${location.search}${location.hash}`);
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // If in guest/demo mode but not allowed for this route, redirect to login
  if (coursesMode !== 'auth' && !allowGuest) {
    return <Navigate to="/login" replace />;
  }

  // If route requires Canvas credentials, check based on mode
  // IMPORTANT: Skip credential checks entirely in demo mode (it uses mock data)
  // Task 6.0e: offline mode is exempt for the same reason demo is -- its data
  // is already loaded and needs no Canvas credentials. Without this exemption
  // every course route would bounce an offline user to /token-setup, which is
  // the page they could not complete in the first place.
  if (requireCanvas && coursesMode !== 'demo' && coursesMode !== 'offline') {
    if (coursesMode === 'guest') {
      // For guest mode, check if we have active credentials and no pending user selection
      const credentials = getActiveCredentials();
      if (!credentials || pendingUserSelection) {
        return <Navigate to="/guest" replace />;
      }
    } else {
      // For authenticated users, check if they have Canvas credentials set up
      if (!hasCanvasCredentials()) {
        return <Navigate to="/token-setup" replace />;
      }
    }
  }

  // User has necessary access and credentials
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requireCanvas: PropTypes.bool,
  allowGuest: PropTypes.bool
};

export default ProtectedRoute;